/**
 * 批量打印订单弹窗
 * 选择多个订单后，逐个拉取详情并渲染打印内容
 */
import React from 'react'
import { Modal, Button, Spin, message } from 'antd'
import { PrinterOutlined } from '@ant-design/icons'
import * as R from 'ramda'
import {
  getMaternityCenterOrderProfile,
  getMaternityCenterChanKangProjects,
  getMaternityCenterOrderProjectArray,
  getMaternityCenterOrderPromotion,
  getMaternityCenterOrderGift,
  getMaternityCenterOrderVoucher,
  normalizeMaternityOrderDetailFromApiResult
} from '@/service/orderTypeApis'
import { buildPrintDocumentHtml } from './buildOrderPrintHtml'
import './BatchOrderPrintModal.less'

const processChanKangList = (list) => {
  const raw = Array.isArray(list) ? list : (list?.data || [])
  const arr = Array.isArray(raw) ? raw : []
  const [givingProjectList, nonGivingList] = R.partition(
    (r) => (r?.typeName ?? '').includes('赠送'),
    arr
  )
  const projectArray = R.sort((a, b) => {
    const typeA = String(a?.typeName ?? '')
    const typeB = String(b?.typeName ?? '')
    if (typeA !== typeB) return typeA.localeCompare(typeB)
    const useNumA = Number(a?.useNum)
    const useNumB = Number(b?.useNum)
    return (Number.isFinite(useNumB) ? useNumB : 0) - (Number.isFinite(useNumA) ? useNumA : 0)
  }, nonGivingList)
  return { data: arr, givingProjectList, projectArray }
}

const processProjectArrayByType = (list) => {
  const raw = Array.isArray(list) ? list : (list?.data || [])
  const arr = Array.isArray(raw) ? raw : []
  const grouped = R.groupBy((r) => String(r?.projectType ?? ''), arr)
  return {
    type1: grouped['1'] || [],
    type2: grouped['2'] || [],
    type3: grouped['3'] || []
  }
}

class BatchOrderPrintModal extends React.Component {
  state = {
    loading: false,
    orderDataMap: {} // { orderId: { orderDetail, maternityCenterChanKangProjects, projectArrayObj, promotionObj, giftObj } }
  }

  componentDidUpdate(prevProps) {
    const { visible, orderIds } = this.props
    if (visible && !prevProps.visible && Array.isArray(orderIds) && orderIds.length > 0) {
      this.fetchAllOrdersData()
    }
    if (!visible && prevProps.visible) {
      this.setState({ orderDataMap: {} })
    }
  }

  fetchAllOrdersData = async () => {
    const { orderIds } = this.props
    if (!Array.isArray(orderIds) || orderIds.length === 0) return

    this.setState({ loading: true, orderDataMap: {} })

    const orderDataMap = {}
    const ids = [...orderIds]

    for (let i = 0; i < ids.length; i++) {
      const orderId = ids[i]
      try {
        const [profileRes, chanKangRes, projectRes, promotionRes, giftRes, voucherRes] = await Promise.all([
          getMaternityCenterOrderProfile(orderId),
          getMaternityCenterChanKangProjects(orderId),
          getMaternityCenterOrderProjectArray(orderId),
          getMaternityCenterOrderPromotion(orderId),
          getMaternityCenterOrderGift(orderId),
          getMaternityCenterOrderVoucher(orderId)
        ])

        const orderDetail = normalizeMaternityOrderDetailFromApiResult(profileRes)
        const chanKangProcessed = processChanKangList(chanKangRes?.data)
        const projectArrayObj = processProjectArrayByType(projectRes?.data)
        const promotionObj = { data: Array.isArray(promotionRes?.data) ? promotionRes.data : (promotionRes?.data?.data ?? []) }
        const giftObj = { data: Array.isArray(giftRes?.data) ? giftRes.data : (giftRes?.data?.data ?? []) }
        const voucherObj = { data: Array.isArray(voucherRes?.data) ? voucherRes.data : (voucherRes?.data?.data ?? []) }

        orderDataMap[orderId] = {
          orderDetail,
          maternityCenterChanKangProjects: chanKangProcessed,
          projectArrayObj,
          promotionObj,
          giftObj,
          voucherObj
        }
      } catch (err) {
        message.error(`订单 ${orderId} 加载失败：${err?.message || '未知错误'}`)
      }
    }

    this.setState({ loading: false, orderDataMap })
  }

  handlePrint = () => {
    const { orderIds = [] } = this.props
    const { orderDataMap } = this.state
    const ids = Array.isArray(orderIds) ? orderIds : []
    const ordersData = ids.map((orderId) => orderDataMap[orderId]).filter(Boolean)
    if (ordersData.length === 0) return

    const html = buildPrintDocumentHtml(ordersData)
    const printWin = window.open('', '_blank')
    if (!printWin) {
      message.warning('请允许弹窗以进行打印')
      return
    }
    printWin.document.write(html)
    printWin.document.close()
    printWin.focus()
    setTimeout(() => {
      printWin.print()
      printWin.onafterprint = () => printWin.close()
    }, 100)
  }

  render() {
    const { visible, orderIds = [], onClose } = this.props
    const { loading, orderDataMap } = this.state

    const ids = Array.isArray(orderIds) ? orderIds : []
    const hasData = ids.length > 0 && Object.keys(orderDataMap).length > 0

    return (
      <Modal
        title="批量打印"
        open={visible}
        onCancel={onClose}
        width={900}
        className="batch-order-print-modal"
        footer={
          <div className="no-print">
            <Button onClick={onClose}>关闭</Button>
            <Button type="primary" icon={<PrinterOutlined />} onClick={this.handlePrint} disabled={!hasData}>
              打印
            </Button>
          </div>
        }
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin size="large" tip="正在加载订单数据..." />
          </div>
        ) : (
          <div id="batch-print-content" className="batch-print-content">
            <iframe
              srcDoc={buildPrintDocumentHtml(ids.map((orderId) => orderDataMap[orderId]).filter(Boolean))}
              title="打印预览"
              className="batch-print-iframe"
            />
          </div>
        )}
      </Modal>
    )
  }
}

export default BatchOrderPrintModal
